/* Language Switcher — checkbox-style picker that closes & goes back to dashboard.
 * Used in profile menu / sidebar. Saves to localStorage via i18next-browser-languagedetector.
 */
import { useTranslation } from "react-i18next";
import { Globe, Check, X } from "lucide-react";
import { LANGUAGES } from "@/i18n";

export default function LanguageSwitcher({ open, onClose }) {
    const { i18n, t } = useTranslation();
    if (!open) return null;
    const currentLang = i18n.language?.split("-")[0] || "en";

    const pick = (code) => {
        i18n.changeLanguage(code);
        try { localStorage.setItem("gtw_language", code); } catch { /* ignore */ }
        // Close and stay on current page (clients land on dashboard naturally)
        setTimeout(() => onClose?.(), 200);
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={onClose}
            data-testid="lang-switcher-modal"
        >
            <div
                className="bg-white w-full sm:max-w-md sm:rounded-lg rounded-t-2xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 border-b border-[#F4F4F5]">
                    <div className="flex items-center gap-2">
                        <Globe size={18} className="text-[#C5A059]" />
                        <h2 className="font-semibold text-[#0A1128]">{t("language_switcher.title")}</h2>
                    </div>
                    <button onClick={onClose} className="text-[#71717A] hover:text-[#0A1128] p-1" data-testid="lang-close">
                        <X size={18} />
                    </button>
                </div>

                <p className="text-xs text-[#71717A] px-4 pt-3">{t("language_switcher.subtitle")}</p>

                <div className="p-4 space-y-2">
                    {LANGUAGES.map((lang) => {
                        const checked = currentLang === lang.code;
                        return (
                            <button
                                key={lang.code}
                                onClick={() => pick(lang.code)}
                                className={`w-full flex items-center justify-between p-4 border-2 transition-colors ${
                                    checked
                                        ? "border-[#C5A059] bg-[#FDF8E8]"
                                        : "border-[#E4E4E7] hover:border-[#C5A059]"
                                }`}
                                data-testid={`lang-option-${lang.code}`}
                            >
                                <div className="flex flex-col items-start">
                                    <span className="text-lg font-medium text-[#0A1128]">{lang.native}</span>
                                    <span className="text-[10px] uppercase tracking-wider text-[#71717A]">{lang.name}</span>
                                </div>
                                <div
                                    className={`w-6 h-6 border-2 flex items-center justify-center ${
                                        checked ? "bg-[#C5A059] border-[#C5A059]" : "border-[#A1A1AA]"
                                    }`}
                                >
                                    {checked && <Check size={14} className="text-white" />}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
